import { useState } from "react";
import { Lightning, ArrowRight, Sparkle, X } from "@phosphor-icons/react";
import api from "../../lib/api";
import { fmtMoney } from "../../lib/finance-format";
import { toast } from "sonner";

/**
 * "¿Qué hago con este dinero?" widget.
 * User types an amount → backend returns top 3 rule-based recommendations.
 */
export default function QuickAdvice({ scope = "personal" }) {
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleAsk = async (e) => {
    e?.preventDefault();
    const n = parseFloat(amount);
    if (!n || n <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/finance/quick-advice", {
        amount: n,
        scope: scope === "all" ? "personal" : scope,
      });
      setResult(data);
    } catch (err) {
      toast.error("No se pudo obtener recomendación");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setAmount("");
    setResult(null);
  };

  return (
    <div
      data-testid="quick-advice-widget"
      className="rounded-lg border border-[#FACC15]/20 bg-gradient-to-br from-[#141621] to-[#0F1017] p-5 md:p-6"
      style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), 0 8px 32px rgba(250,204,21,0.06)" }}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15] mb-1">
            <Sparkle size={12} weight="fill" />
            Acción rápida · CFO
          </div>
          <h3 className="text-lg md:text-xl font-heading font-bold text-white">
            ¿Qué hago con este dinero?
          </h3>
        </div>
        {result && (
          <button
            data-testid="quick-advice-reset"
            onClick={reset}
            className="w-8 h-8 rounded-lg hover:bg-white/5 text-white/50 hover:text-white flex items-center justify-center"
          >
            <X size={14} weight="bold" />
          </button>
        )}
      </div>

      {!result ? (
        <form onSubmit={handleAsk} className="space-y-3">
          <label className="block text-[11px] font-mono uppercase tracking-[0.14em] text-white/50">
            Monto disponible
          </label>
          <div className="flex items-stretch gap-2">
            <div className="flex-1 flex items-center gap-2 rounded-lg bg-black/40 border border-white/10 focus-within:border-[#FACC15]/60 px-4 py-3 transition">
              <span className="text-white/40 font-mono text-lg">$</span>
              <input
                data-testid="quick-advice-input"
                type="number"
                inputMode="decimal"
                step="any"
                placeholder="500"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="flex-1 bg-transparent outline-none text-white font-mono font-bold text-xl tabular-nums placeholder-white/20"
              />
            </div>
            <button
              data-testid="quick-advice-submit"
              type="submit"
              disabled={loading || !amount}
              className="px-5 rounded-lg bg-[#FACC15] hover:bg-[#EAB308] disabled:opacity-40 disabled:cursor-not-allowed text-black font-heading font-bold flex items-center gap-2 transition"
            >
              {loading ? (
                <Lightning size={16} weight="fill" className="animate-spin" />
              ) : (
                <>
                  <span className="hidden sm:inline text-sm">Analizar</span>
                  <ArrowRight size={16} weight="bold" />
                </>
              )}
            </button>
          </div>
          <p className="text-[11px] font-mono text-white/40 leading-relaxed">
            El CFO analiza tu deuda, fondo de emergencia y capacidad de inversión, y te dice
            <span className="text-[#FACC15]"> exactamente dónde meterlo</span>.
          </p>
        </form>
      ) : (
        <div className="space-y-3">
          <div className="text-[11px] font-mono uppercase tracking-wider text-white/50">
            Con <span className="text-white font-bold">{fmtMoney(result.amount)}</span> tu CFO recomienda:
          </div>
          {result.recommendations.map((r, idx) => (
            <div
              key={idx}
              data-testid={`advice-rec-${idx}`}
              className={`p-4 rounded-lg border ${
                idx === 0
                  ? "border-[#FACC15]/40 bg-[#FACC15]/6"
                  : "border-white/10 bg-black/30"
              }`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`text-[9px] font-mono uppercase tracking-[0.2em] font-bold px-1.5 py-0.5 rounded ${
                        idx === 0
                          ? "bg-[#FACC15] text-black"
                          : "bg-white/10 text-white/60"
                      }`}
                    >
                      #{idx + 1} {idx === 0 ? "MEJOR" : "OPCIÓN"}
                    </span>
                  </div>
                  <div className="text-sm md:text-base font-heading font-bold text-white leading-tight">
                    {r.title}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-lg font-mono font-bold text-white tabular-nums">
                    {fmtMoney(r.amount)}
                  </div>
                </div>
              </div>
              <p className="text-[12px] text-white/60 leading-relaxed mb-2">{r.reason}</p>
              <div className="text-[11px] font-mono text-emerald-400 font-bold">
                → {r.impact_label}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
